interface ProgressState {
  received: number;
  length: number;
  loading: boolean;
  readonly progress: number;
}

type ProgressDispatcher = Window | typeof globalThis | HTMLElement;

export async function progressFetch(
  this: ProgressDispatcher | undefined,
  resource: RequestInfo | URL,
  options?: RequestInit,
) {
  const response = await fetch(resource, options);

  if (response.status >= 200 && response.status < 300) {
    return handleFetchResponse.call(this, response);
  }

  return Promise.reject(response);
}

async function handleFetchResponse(this: ProgressDispatcher | undefined, response: Response) {
  if (!response.body) {
    return response;
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];

  const state = new Proxy<ProgressState>(
    {
      received: 0,
      length: Number(response.headers.get('content-length')) || 0,
      loading: true,
      progress: 0,
    },
    {
      get(target, prop, receiver) {
        if (prop === 'progress') {
          return target.length === 0 ? 0 : Math.ceil((target.received * 100) / target.length);
        }

        return Reflect.get(target, prop, receiver);
      },
    },
  );

  const dispatcher: ProgressDispatcher = (this ?? window) as ProgressDispatcher;
  dispatcher.dispatchEvent(new CustomEvent('fetch-start', { detail: state }));

  while (state.loading) {
    const { done, value } = await reader.read();

    if (done) {
      state.loading = false;
      dispatcher.dispatchEvent(new CustomEvent('fetch-done', { detail: state }));
    } else if (value) {
      chunks.push(value);
      state.received += value.length;
      dispatcher.dispatchEvent(new CustomEvent('fetch-progress', { detail: state }));
    }
  }

  return prepareResponse(response, {
    chunks,
    length: state.received,
  });
}

function prepareResponse(response: Response, { chunks, length }: { chunks: Uint8Array[]; length: number }) {
  const body = new Uint8Array(length);
  const type = response.headers.get('content-type') ?? undefined;

  let position = 0;

  for (const chunk of chunks) {
    body.set(chunk, position);
    position += chunk.length;
  }

  return {
    ...response,
    blob: () => Promise.resolve(new Blob([body.buffer], { type })),
    json: () => {
      try {
        return Promise.resolve(JSON.parse(new TextDecoder('utf-8').decode(body)));
      } catch (e) {
        return Promise.reject(new Error(`[ProgressFetch] Failed to parse JSON response: ${(e as Error).message}`));
      }
    },
    text: () => Promise.resolve(new TextDecoder('utf-8').decode(body)),
  };
}
